import { z } from 'zod'

import { ActivityActionType, FormFieldType } from '../enums'

export const activitySchema = z.object({
  id: z.string().uuid().optional(),          // backend does not always return id
  actorName: z.string(),
  actionType: z.nativeEnum(ActivityActionType),
  note: z.string().nullable(),
  fieldLabel: z.string().nullable(),
  fieldType: z.enum(Object.values(FormFieldType) as [string, ...string[]]).nullable().optional(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  timestamp: z.string(), // accept any ISO-8601 string the backend returns
  sessionId: z.string().uuid().nullable(),
  // Phase 4 additions
  tagId: z.string().uuid().nullable().optional(),
  tagLabel: z.string().nullable().optional(),
  tagColorHex: z.string().nullable().optional(),
  // Follow-up tag (on completion/reopen/add-note entries)
  followUpTagId: z.string().uuid().nullable().optional(),
  followUpTagLabel: z.string().nullable().optional(),
  followUpTagColorHex: z.string().nullable().optional(),
  // FILE_UPLOAD attachment snapshot (null for other action types)
  attachmentKey: z.string().nullable().optional(),
  attachmentName: z.string().nullable().optional(),
  attachmentSizeBytes: z.number().int().nonnegative().nullable().optional(),
  attachmentContentType: z.string().nullable().optional(),
  // CALL entries: the call they summarize
  callId: z.string().uuid().nullable().optional(),
  // A change to a field the viewer may not see: the entry stays, without the field name or values
  redacted: z.boolean().optional(),
})

export const activityListResponseSchema = z.object({
  activitySessionId: z.string().uuid().nullable(),
  nextActivityAtReminder: z.string().nullable(),
  // Phase 4 addition
  nextActivityInstructions: z.string().nullable().optional(),
  // Follow-up tag at session level
  followUpTagId: z.string().uuid().nullable().optional(),
  followUpTagLabel: z.string().nullable().optional(),
  followUpTagColorHex: z.string().nullable().optional(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  activities: z.array(activitySchema),
})

export const addActivityRequestSchema = z.object({
  note: z.string().min(1),
  recordActivitySessionId: z.string().uuid(),
  // nextActivityAt is optional on the request; only include when user explicitly picks a date
  nextActivityAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  // Phase 4 additions — instructions only sent when nextActivityAt is also set
  tagId: z.string().uuid().optional(),
  nextActivityInstructions: z.string().optional(),
  // Follow-up tag — only when nextActivityAt is set
  followUpTagId: z.string().uuid().optional(),
})

export type Activity = z.infer<typeof activitySchema>
export type ActivityListResponse = z.infer<typeof activityListResponseSchema>
export type AddActivityRequest = z.infer<typeof addActivityRequestSchema>
